import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import React from 'react';

import * as AnimationComponents from '../../animations';
import Author from '../partials/Author';
import Category from '../partials/Category';
import Date from '../partials/Date';
import Image from '../partials/Image';
import Sponsor from '../partials/Sponsor';
import Surtitle from '../partials/Surtitle';
import DocumentMetadata from '../typography/DocumentMetadata';
import DocumentSubtitle from '../typography/DocumentSubtitle';
import DocumentTitle from '../typography/DocumentTitle';
import ImageCaption from '../typography/ImageCaption';

import styles from '../../styles/headers/document-default-header.module.css';

export interface DocumentDefaultHeaderProps {
    id?: string | null;
    slug?: string | null;
    type?: string | null;
    sponsors?: Sponsor[];
    brand?: Brand;
    date?: string | null;
    title?: string | null;
    subtitle?: string | null;
    surtitle?: string | null;
    image?: Image;
    coverImage?: Image;
    categories?: Category[];
    authors?: Author[];
    placeholder?: boolean;
    animation?: string | null;
    withoutImage?: boolean;
    className?: string | null;
}

const DocumentDefaultHeader = ({
    id = null,
    slug = null,
    type = null,
    title = null,
    surtitle = null,
    subtitle = null,
    brand = null,
    image = null,
    categories = null,
    authors = null,
    date = null,
    sponsors = null,
    placeholder = false,
    animation = null,
    withoutImage = false,
    className = null,
}: DocumentDefaultHeaderProps) => {
    const [category = null] = categories || [];
    const finalAuthors =
        authors !== null
            ? authors.filter(({ isPerson = true }) => isPerson)
            : placeholder
              ? [null]
              : [];
    const { handle: brandHandle } = brand || {};
    const [sponsor = null] = sponsors || [];

    const hasAuthor = finalAuthors.length > 0;
    const withBefore = category !== null || surtitle !== null;
    const withAfter = hasAuthor || date !== null || subtitle !== null || sponsor !== null;

    const AnimationComponent = getComponentFromName(AnimationComponents, animation, null);

    const { credits: imageCredits = null } = image || {};

    return (
        <div
            className={classNames([
                styles.container,
                'default-header-container',
                {
                    [styles[brandHandle]]: brandHandle !== null,
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.inner}>
                {AnimationComponent !== null ? <AnimationComponent /> : null}

                {!withoutImage && AnimationComponent === null ? (
                    <div
                        className={classNames([
                            styles.imageContainer,
                            'default-header-image-container',
                        ])}
                    >
                        <Image
                            className={classNames([styles.image, 'default-header-image'])}
                            pictureClassName={styles.picture}
                            media={image}
                            size="responsive"
                            fetchPriority="high"
                            withoutSize
                            withPlaceholder
                        />
                    </div>
                ) : null}

                {!withoutImage && AnimationComponent === null && imageCredits !== null ? (
                    <ImageCaption tag="div" className={styles.imageCredits} html={imageCredits} />
                ) : null}

                <div
                    className={classNames([
                        styles.content,
                        'default-header-content',
                        {
                            [styles.withBefore]: withBefore,
                            [styles.withAfter]: withAfter,
                        },
                    ])}
                >
                    {withBefore || placeholder ? (
                        <div className={styles.before}>
                            {category !== null || placeholder ? (
                                <Category
                                    {...category}
                                    placeholder={placeholder}
                                    className={classNames([
                                        styles.category,
                                        {
                                            [styles.placeholder]: placeholder,
                                        },
                                    ])}
                                    withLink
                                    clickRef="document-header"
                                />
                            ) : null}
                            <Surtitle
                                surtitle={surtitle}
                                placeholder={placeholder}
                                className={classNames([styles.surtitle, 'default-header-surtitle'])}
                                withLink
                                clickRef="document-header"
                            />
                        </div>
                    ) : null}
                    <DocumentTitle
                        className={styles.title}
                        html={title}
                        placeholder={placeholder}
                    />
                    {withAfter || placeholder ? (
                        <div className={styles.after}>
                            {subtitle !== null || placeholder ? (
                                <DocumentSubtitle
                                    className={classNames([
                                        styles.subtitle,
                                        'default-header-subtitle',
                                    ])}
                                    placeholder={placeholder}
                                >
                                    {subtitle}
                                </DocumentSubtitle>
                            ) : null}

                            {hasAuthor ? (
                                <div className={styles.authors}>
                                    {authors.map((author, index) => (
                                        <Author
                                            key={`author-${index}`}
                                            {...author}
                                            clickRef="document-header"
                                            prefix={index > 0 ? 'et' : 'Par'}
                                            placeholder={placeholder}
                                            className={styles.author}
                                            nameClassName={styles.authorName}
                                            initialsClassName={styles.authorInitials}
                                            withRole
                                            withLink
                                        />
                                    ))}
                                </div>
                            ) : null}

                            {date !== null || placeholder ? (
                                <DocumentMetadata
                                    className={classNames([
                                        styles.date,
                                        {
                                            [styles.hasAuthor]: hasAuthor,
                                        },
                                    ])}
                                    placeholder={placeholder}
                                >
                                    <Date date={date} />
                                </DocumentMetadata>
                            ) : null}

                            {sponsor !== null ? (
                                <Sponsor {...sponsor} withImage className={styles.sponsor} />
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default DocumentDefaultHeader;
